import { useState, useRef, Fragment } from 'react';
import { Dialog, Transition, Tab } from '@headlessui/react';
import {
  PhotoIcon,
  LinkIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { getAuthToken } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/constants';
import { clsx } from 'clsx';

interface ImageInsertDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (data: { src: string; alt: string; caption: string }) => void;
  postTitle?: string;
}

const ImageInsertDialog = ({
  isOpen,
  onClose,
  onInsert,
  postTitle,
}: ImageInsertDialogProps) => {
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [pastedUrl, setPastedUrl] = useState('');
  const [alt, setAlt] = useState('');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [generatingAlt, setGeneratingAlt] = useState(false);
  const [generatingCaption, setGeneratingCaption] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentSrc = activeTab === 0 ? uploadedUrl : pastedUrl;

  const reset = () => {
    setUploadedUrl('');
    setPastedUrl('');
    setAlt('');
    setCaption('');
    setError(null);
    setUploading(false);
    setGeneratingAlt(false);
    setGeneratingCaption(false);
    setActiveTab(0);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be less than 10MB');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(API_ENDPOINTS.upload, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        setUploadedUrl(result.data.url);
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleGenerateAlt = async () => {
    if (!currentSrc) return;
    setGeneratingAlt(true);
    setError(null);

    try {
      const token = getAuthToken();
      const prompt = postTitle
        ? `I have a photo for a post titled "${postTitle}". Generate a concise, descriptive alt text for this image. Return ONLY the alt text string, no quotes, no explanation.`
        : `Generate a concise, descriptive alt text for a photo. Return ONLY the alt text string, no quotes, no explanation.`;

      const response = await fetch(`${API_ENDPOINTS.ai}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt, type: 'custom' }),
      });

      const result = await response.json();

      if (result.success) {
        let text = result.data.content.trim();
        text = text.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '');
        text = text.replace(/^["']|["']$/g, '');
        setAlt(text);
      } else {
        setError(result.error || 'AI generation failed');
      }
    } catch {
      setError('Failed to generate alt text');
    } finally {
      setGeneratingAlt(false);
    }
  };

  const handleGenerateCaption = async () => {
    if (!postTitle) return;
    setGeneratingCaption(true);
    setError(null);

    try {
      const token = getAuthToken();
      const prompt = `I have a photo for a post titled "${postTitle}". Generate an engaging image caption (1-2 sentences) that relates to the post topic. Return ONLY the caption text, no quotes, no explanation.`;

      const response = await fetch(`${API_ENDPOINTS.ai}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt, type: 'custom' }),
      });

      const result = await response.json();

      if (result.success) {
        let text = result.data.content.trim();
        text = text.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '');
        text = text.replace(/^["']|["']$/g, '');
        setCaption(text);
      } else {
        setError(result.error || 'AI generation failed');
      }
    } catch {
      setError('Failed to generate caption');
    } finally {
      setGeneratingCaption(false);
    }
  };

  const handleInsert = () => {
    if (!currentSrc) return;
    onInsert({ src: currentSrc, alt, caption });
    handleClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    Insert Image
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
                  <Tab.List className="flex space-x-1 rounded-lg bg-gray-100 p-1 mb-4">
                    <Tab
                      className={({ selected }) =>
                        clsx(
                          'w-full rounded-md py-2 text-sm font-medium transition-colors',
                          selected
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-800'
                        )
                      }
                    >
                      <PhotoIcon className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                      Upload
                    </Tab>
                    <Tab
                      className={({ selected }) =>
                        clsx(
                          'w-full rounded-md py-2 text-sm font-medium transition-colors',
                          selected
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-800'
                        )
                      }
                    >
                      <LinkIcon className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                      URL
                    </Tab>
                  </Tab.List>

                  <Tab.Panels>
                    {/* Upload Tab */}
                    <Tab.Panel>
                      {uploadedUrl ? (
                        <div className="relative">
                          <img
                            src={uploadedUrl}
                            alt="Preview"
                            className="w-full h-48 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => setUploadedUrl('')}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <XMarkIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          onDragOver={e => e.preventDefault()}
                          onDrop={e => {
                            e.preventDefault();
                            const files = e.dataTransfer.files;
                            if (files.length > 0 && fileInputRef.current) {
                              const dt = new DataTransfer();
                              dt.items.add(files[0]);
                              fileInputRef.current.files = dt.files;
                              fileInputRef.current.dispatchEvent(
                                new Event('change', {
                                  bubbles: true,
                                })
                              );
                            }
                          }}
                          className="relative border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 transition-colors"
                        >
                          <PhotoIcon className="mx-auto h-10 w-10 text-gray-400" />
                          <p className="text-sm text-gray-600 mt-2">
                            Click or drag to upload
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            PNG, JPG, GIF up to 10MB
                          </p>

                          {uploading && (
                            <div className="absolute inset-0 bg-white/80 rounded-lg flex items-center justify-center">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900" />
                            </div>
                          )}
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                        disabled={uploading}
                      />
                    </Tab.Panel>

                    {/* URL Tab */}
                    <Tab.Panel>
                      <input
                        type="url"
                        value={pastedUrl}
                        onChange={e => setPastedUrl(e.target.value)}
                        placeholder="https://example.com/image.jpg"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                      />
                      {pastedUrl && (
                        <img
                          src={pastedUrl}
                          alt="Preview"
                          className="w-full h-48 object-cover rounded-lg mt-3"
                          onError={e =>
                            (e.currentTarget.style.display = 'none')
                          }
                          onLoad={e => (e.currentTarget.style.display = '')}
                        />
                      )}
                    </Tab.Panel>
                  </Tab.Panels>
                </Tab.Group>

                {/* Alt text */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-700">
                      Alt text
                    </label>
                    {currentSrc && (
                      <button
                        type="button"
                        onClick={handleGenerateAlt}
                        disabled={generatingAlt}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 transition-colors disabled:opacity-50"
                      >
                        <SparklesIcon className="w-3 h-3" />
                        {generatingAlt ? 'Generating...' : 'AI'}
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={alt}
                    onChange={e => setAlt(e.target.value)}
                    placeholder="Describe the image for accessibility..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                {/* Caption */}
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-700">
                      Caption
                    </label>
                    {currentSrc && postTitle && (
                      <button
                        type="button"
                        onClick={handleGenerateCaption}
                        disabled={generatingCaption}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 transition-colors disabled:opacity-50"
                      >
                        <SparklesIcon className="w-3 h-3" />
                        {generatingCaption ? 'Generating...' : 'AI'}
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={caption}
                    onChange={e => setCaption(e.target.value)}
                    placeholder="Caption text below the image (optional)..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                  />
                </div>

                {/* Error */}
                {error && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-2">
                    <p className="text-xs text-red-600">{error}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleInsert}
                    disabled={!currentSrc}
                    className="px-4 py-2 text-sm font-medium text-white bg-ocean-600 rounded-lg hover:bg-ocean-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Insert
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ImageInsertDialog;
